import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { pastel, ink, entryHue } from "../lib/planningColors";
import { REMINDER_COLOR } from "../lib/reminders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** "2026-09-15" for a Date, in local terms rather than through UTC, which
 *  would move the day across the date line for anybody west of Greenwich. */
function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// ISO 8601 week number: Monday-start weeks, week 1 is the one containing the
// year's first Thursday. Same convention as the "CW" column in the planning
// sheet, so a number here means what it meant there.
function isoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Anonymous Gregorian computus. Easter is the one Italian holiday that moves,
// and Easter Monday is the day actually taken off, so it is derived rather
// than listed: nobody has to maintain a table each January.
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Italian public holidays for a year, as a map of "M-D" to a label. */
function italianHolidays(year) {
  const easterMonday = new Date(easterSunday(year));
  easterMonday.setDate(easterMonday.getDate() + 1);

  const entries = [
    [0, 1, "Capodanno"],
    [0, 6, "Epifania"],
    [3, 25, "Festa della Liberazione"],
    [4, 1, "Festa del lavoro"],
    [5, 2, "Festa della Repubblica"],
    [7, 15, "Ferragosto"],
    [10, 1, "Ognissanti"],
    [11, 8, "Immacolata Concezione"],
    [11, 25, "Natale"],
    [11, 26, "Santo Stefano"],
    [easterMonday.getMonth(), easterMonday.getDate(), "Luned\u00ec dell'Angelo"],
  ];

  const map = {};
  for (const [month, day, label] of entries) map[`${month}-${day}`] = label;
  return map;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * A compact month calendar with the ISO week number beside every row.
 *
 * Sized to sit next to the activity band: the week number is the point, the
 * grid is what makes it legible. Holidays are computed rather than listed, so
 * nothing here needs maintaining in January and Easter is right every year.
 */
export default function WeekCalendar() {
  const { language } = useLanguage();
  const s = localStrings(language);

  // Every date on this panel follows the language chosen in Core, not the one
  // the machine is set to. The month name is on the home page at all times, so
  // it is the most visible date in the application: resolved against the
  // operating system it read "August 2026" in the middle of an Italian page.
  const locale = language === "it" ? "it-IT" : "en-US";

  const [cursor, setCursor] = useState(() => new Date());
  const [hovered, setHovered] = useState(null);
  const today = new Date();

  const holidays = useMemo(() => italianHolidays(cursor.getFullYear()), [cursor]);

  // The account's own row of the planning, by date, one year at a time.
  //
  // Held per year and never asked for twice, because paging back and forth
  // across December is one gesture and would otherwise be a request each way.
  // The server reads the same cached workbook the Planning page reads, so the
  // cost of a miss is a parse it was going to do anyway.
  //
  // Every failure is silence. The workbook may be missing for a year, Graph may
  // be down, the secret may have expired, the account may match no column at
  // all - and in each case this is the calendar it has always been. It sits at
  // the top of the page everybody opens first, so it does not get to depend on
  // Microsoft being reachable.
  const [planning, setPlanning] = useState({});
  const asked = useRef({});
  const year = cursor.getFullYear();

  useEffect(() => {
    if (asked.current[year]) return undefined;
    asked.current[year] = true;

    let cancelled = false;
    axios
      .get(`${API}/planning/mine`, { params: { year }, withCredentials: true })
      .then((res) => {
        if (!cancelled) setPlanning((prev) => ({ ...prev, [year]: res.data?.days || {} }));
      })
      .catch(() => {
        if (!cancelled) setPlanning((prev) => ({ ...prev, [year]: {} }));
      });

    return () => { cancelled = true; };
  }, [year]);

  const entries = planning[year] || {};

  // The account's own open reminders with a due date, by date, a year at a
  // time like the planning. A recurring reminder is on every one of its days.
  // Read again whenever the home page is opened, since this component is
  // mounted afresh each time, which is what keeps it in step with the
  // reminders page without any other signal. Failure is silence here too.
  const [reminders, setReminders] = useState({});
  const askedReminders = useRef({});

  useEffect(() => {
    if (askedReminders.current[year]) return undefined;
    askedReminders.current[year] = true;

    let cancelled = false;
    axios
      .get(`${API}/reminders/calendar`, { params: { year }, withCredentials: true })
      .then((res) => {
        if (!cancelled) setReminders((prev) => ({ ...prev, [year]: res.data?.days || {} }));
      })
      .catch(() => {
        if (!cancelled) setReminders((prev) => ({ ...prev, [year]: {} }));
      });

    return () => { cancelled = true; };
  }, [year]);

  const dueByDay = reminders[year] || {};

  const weeks = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);

    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - ((firstOfMonth.getDay() + 6) % 7));

    const rows = [];
    const walker = new Date(gridStart);
    for (let row = 0; row < 6; row++) {
      const days = [];
      for (let col = 0; col < 7; col++) {
        days.push(new Date(walker));
        walker.setDate(walker.getDate() + 1);
      }
      // A week that starts after the month has ended belongs entirely to the
      // next one and is not drawn. The test runs before the row is kept rather
      // than after it: kept first, a month ending on a Sunday gained a whole
      // grey week of the month after it, week number included. February 2027
      // is the next month that does it.
      if (row > 0 && days[0].getMonth() !== month) break;
      rows.push({ week: isoWeek(days[0]), days });
    }
    return rows;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const dayLabels = useMemo(() => {
    // Read from a known Monday rather than hardcoded, so the initials are
    // whatever the chosen language uses.
    const base = new Date(2026, 0, 5);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      return d.toLocaleDateString(locale, { weekday: "narrow" });
    });
  }, [locale]);

  // What the strip under the grid says, for the day being pointed at or for
  // today when nothing is. Reserving the line means the panel never changes
  // height as the pointer moves.
  //
  // The words from the sheet come first when there are any: they are the only
  // part of this panel that is about the person reading it, and the one thing
  // the grid itself has no room to say. The date follows, and a holiday name
  // sits between them when the two coincide.
  //
  // This is also where the entry is legible at all. A month cell is forty-odd
  // pixels wide, so the grid carries the colour and this carries the name; any
  // attempt to put "Sumitomo Japan" in the square is arithmetic, not design.
  const footerDay = hovered || today;
  const footerEntry = entries[isoDate(footerDay)] || null;
  const footerDue = (footerDay.getFullYear() === year ? dueByDay[isoDate(footerDay)] : null) || [];
  const footerHoliday = holidays[`${footerDay.getMonth()}-${footerDay.getDate()}`];

  // The planning's words first, then the day's reminders with their time when
  // they have one, then the holiday and the date.
  const footerText = (() => {
    const dateText = footerDay.toLocaleDateString(locale, {
      weekday: "long", day: "numeric", month: "long",
    });
    const parts = [];
    if (footerEntry) parts.push(footerEntry.activity);
    for (const r of footerDue) parts.push(r.time ? `${r.time} ${r.title}` : r.title);
    if (footerHoliday) parts.push(footerHoliday);
    parts.push(dateText);
    return parts.join(" \u00b7 ");
  })();

  // The entry's own colour when there is one, so the line agrees with the
  // square above it; the reminders' colour when they are all the day has.
  // Otherwise the holiday rule the panel already had.
  const footerStyle = footerEntry
    ? { color: ink(entryHue(footerEntry)) }
    : footerDue.length
    ? { color: REMINDER_COLOR }
    : undefined;
  const footerHasWords = Boolean(footerEntry) || footerDue.length > 0;

  return (
    <section data-testid="week-calendar" className="flex flex-col h-full">
      {/* Same fixed height as the activity band's heading row beside it, so
          the two labels sit on one line.

          Indigo rather than the grey every other label wears: it is already
          the colour of the CW column, the week numbers, today's marker and
          the hover ring, so the heading agrees with the panel beneath it. */}
      <div className="flex items-center gap-1.5 mb-2 h-7 shrink-0">
        <CalendarDays className="w-3.5 h-3.5 text-indigo-500" />
        <span className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
          {s("cal.title")}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-3 shadow-sm flex-1 min-h-0 flex flex-col">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <span className="text-xs font-semibold text-slate-700 capitalize">{monthLabel}</span>
          <div className="flex gap-0.5">
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
              aria-label={s("feed.previous")}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
              aria-label={s("feed.next")}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* The grid takes the slack, so the panel matches whatever height the
            row settles on instead of leaving a gap under the last week. */}
        <div
          className="grid grid-cols-[26px_repeat(7,1fr)] items-center text-center gap-y-0.5 flex-1 content-evenly"
          onMouseLeave={() => setHovered(null)}
        >
          <span className="text-[9px] font-bold text-indigo-500 tracking-wider">CW</span>
          {dayLabels.map((d, i) => (
            <span
              key={i}
              className={`text-[9px] font-semibold uppercase ${i >= 5 ? "text-rose-400" : "text-slate-400"}`}
            >
              {d}
            </span>
          ))}

          {weeks.map((row) => (
            <Row
              key={row.week}
              row={row}
              cursor={cursor}
              today={today}
              holidays={holidays}
              entries={entries}
              dueByDay={dueByDay}
              hovered={hovered}
              onHover={setHovered}
            />
          ))}
        </div>

        {/* One reserved line rather than a native title attribute: the browser
            tooltip takes a second to appear, styles itself, and cannot say
            anything about a day that is not a holiday. */}
        <div className="mt-2 pt-2 border-t border-slate-100 shrink-0">
          {/* Not capitalised when it carries an entry: the sheet's own words
              are already written the way somebody meant them, and forcing a
              capital turns "NO TRASFERTE" into something it is not. */}
          <p
            data-testid="calendar-footer"
            style={footerStyle}
            className={`text-[11px] truncate ${footerHasWords ? "font-medium" : "capitalize"} ${
              footerHasWords ? "" : footerHoliday && hovered ? "text-rose-600 font-medium" : "text-slate-500"
            }`}
          >
            {footerText}
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({ row, cursor, today, holidays, entries, dueByDay, hovered, onHover }) {
  return (
    <>
      <span className="text-[10px] font-semibold text-indigo-500 bg-indigo-50 rounded py-0.5">
        {row.week}
      </span>
      {row.days.map((d, i) => {
        const inMonth = d.getMonth() === cursor.getMonth();
        const isToday = isSameDay(d, today);
        const holiday = holidays[`${d.getMonth()}-${d.getDate()}`];
        const isWeekend = i >= 5;
        const nonWorking = inMonth && (isWeekend || Boolean(holiday));
        const isHovered = hovered && isSameDay(d, hovered);

        // Only for days of the month being looked at. A trip showing through
        // on the greyed-out tail of the month before would be read as
        // belonging to this one.
        const entry = inMonth ? entries[isoDate(d)] : null;

        // A reminder is a dot in the corner rather than a fill: the fill is the
        // planning's, and a day can carry both.
        const hasDue = inMonth && Boolean(dueByDay[isoDate(d)]?.length);

        // Today keeps its indigo. It is the one mark on this panel that has to
        // survive whatever else is true of the day, and an entry underneath it
        // is still named in the line below, which is where the words live.
        //
        // The fill is set inline so it wins over the hover class: the ring
        // still draws, the colour stays. Set as a class it would be the hover
        // tint that won, and pointing at a day would erase what it says.
        const hue = entry ? entryHue(entry) : null;
        const fill = hue && !isToday ? pastel(hue) : undefined;

        let tone = "text-slate-300";
        if (inMonth) tone = nonWorking ? "text-rose-500 font-medium" : "text-slate-700";

        return (
          <span
            key={i}
            onMouseEnter={() => onHover(d)}
            style={fill ? { backgroundColor: fill, color: ink(hue) } : undefined}
            className={`relative text-[11px] py-0.5 rounded cursor-default transition-colors ${
              isToday
                ? "bg-indigo-600 text-white font-bold"
                : isHovered
                ? "ring-1 ring-inset ring-indigo-300 bg-indigo-50/60"
                : ""
            } ${isToday || fill ? "" : tone} ${fill ? "font-semibold" : ""}`}
          >
            {d.getDate()}
            {inMonth && holiday && !isToday && (
              <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-1 h-1 rounded-full bg-rose-500" />
            )}
            {hasDue && (
              <span
                data-testid={`calendar-reminder-${isoDate(d)}`}
                className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: isToday ? "#FFFFFF" : REMINDER_COLOR }}
              />
            )}
          </span>
        );
      })}
    </>
  );
}
